import { Button } from '@ksyos/design-system';
import type { IMrc } from '../../../../../../common/validation/customControls/pulmonology';
import type { ValidationErrors } from '../../../../../utils/validation';
import { pulmoMrcCustomControlDataSchema } from '../../../../../../common/validation/customControls/pulmonology';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { InputRadioGroup } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { Modal } from '../../../../elements/modals/Modal';
import { AnamneseType } from './anamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IAnamneseMrcProps {
    anamneseData: IMrc | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item: IMrc) => Promise<void>;
}

interface IAnamneseMrcState {
    anamneseData: IMrc;
    errors: ValidationErrors;
}

const answersMrc = [
    'Ik heb geen last van kortademigheid',
    'Ik word alleen kortademig bij zware inspanning',
    'Ik word alleen kortademig als ik me moet haasten op vlak terrein of tegen een lichte helling oploop',
    'Door mijn kortademigheid loop ik op vlak terrein langzamer dan andere mensen van mijn leeftijd, of moet ik stoppen om op adem te komen als ik mijn eigen tempo loop',
    'Na ongeveer 100 meter lopen op vlak terrein of na een paar minuten lopen op vlak terrein moet ik stoppen i.v.m. kortademigheid',
    'Ik ben te kortademig om het huis uit te gaan, of ik ben kortademig tijdens het aan- of uitkleden',
];

export class AnamneseMrc extends Component<IAnamneseMrcProps, IAnamneseMrcState> {
    constructor(props: IAnamneseMrcProps, context?: {}) {
        super(props, context);
        this.state = {
            anamneseData: {
                // eslint-disable-next-line @stylistic/max-len
                bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing:
                    null,
                scoreMrc: null,
                ...(this.props.anamneseData || {}),
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.props.deleteAnamneseData()}
                        variant="outline"
                    >
                        Verwijder
                    </Button>,
                    <Button
                        onClick={async () => await this.validateAnamneseData()}
                        variant="solid"
                    >
                        Anamnese opslaan
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="MRC anamnese"
            >
                <AnamneseWrapper anamneseType={AnamneseType.MRC}>
                    <FormGroup
                        hasErrors={this.state.errors.has(
                            'bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing'
                        )}
                    >
                        <InputRadioGroup
                            automation="kortademig"
                            error={this.state.errors.get(
                                'bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing'
                            )}
                            name="kortademig"
                            onChange={async (value) =>
                                await this.handleChange(
                                    'bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing',
                                    value
                                )
                            }
                            onDoValidate={async () =>
                                await this.validateOne(
                                    'bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing'
                                )
                            }
                            options={answersMrc.map((answerMrc) => answerMrc)}
                            radioButtonsStacked
                            value={
                                this.state.anamneseData
                                    .bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing
                            }
                        />
                    </FormGroup>
                </AnamneseWrapper>
            </Modal>
        );
    }

    private async handleChange<K extends keyof IMrc>(name: K, value: IMrc[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IMrc) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(pulmoMrcCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private async validateAnamneseData() {
        await this.setState(({ anamneseData }) => ({
            anamneseData: { ...anamneseData, scoreMrc: this.getMrcScore(anamneseData) },
        }));
        const { hasErrors, errors, item } = validate(pulmoMrcCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            this.props.setAnamneseData(item as IMrc);
        }
    }

    private getMrcScore(anamneseData: IMrc) {
        const answer = answersMrc.findIndex(
            (answerMrc) =>
                answerMrc ===
                anamneseData.bent_u_wel_eens_kortademig_en_zo_ja_welke_van_de_onderstaande_uitspraken_is_voor_u_het_meest_van_toepassing
        );
        if (answer !== -1) {
            return Number(answer.toFixed(2));
        }

        return null;
    }
}
